import { fetchUser } from '~/actions/user.actions';
import ExternalRedirect from '~/components/ExternalRedirect';
import { ComponentType, useCallback, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { Route, RouteProps } from 'react-router';
import { getUserIsAuthenticated, getUserIsLoading } from '~/selectors/user.selectors';

export interface AuthenticatedRouteProps {
  path: string;
  exact: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  component: JSX.Element;
}

const AuthenticatedRoute = (props: AuthenticatedRouteProps & RouteProps) => {
  const { path, exact, component, location } = props;

  const isLoading = useSelector(getUserIsLoading);
  const isAuthenticated = useSelector(getUserIsAuthenticated);

  const dispatch = useDispatch();
  const authenticateUser = useCallback(() => dispatch(fetchUser()), [dispatch]);

  useEffect(() => {
    if (!isAuthenticated) {
      authenticateUser();
    }
  }, [authenticateUser, isAuthenticated]);

  const renderChildren = () => {
    const RouteComponent = component;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (isAuthenticated) return <RouteComponent {...(props as any)} />;

    // TODO: Replace with spinner or something
    if (isLoading) return null;

    if (!location) return null;

    if (!isLoading && !isAuthenticated) {
      let postLoginRelativeRedirectUrl = '';
      if (location.pathname && location.pathname !== '/') {
        postLoginRelativeRedirectUrl = location.pathname;
      }

      if (location.search) {
        postLoginRelativeRedirectUrl = postLoginRelativeRedirectUrl.concat(location.search);
      }

      let redirectUrl = '';

      if (postLoginRelativeRedirectUrl !== '') {
        // trim the trailing slash so the leading slash in the path does not produce a double slash
        let postLoginAbsoluteRedirectUrl = CONFIG.reactBaseUrl
          .slice(0, -1)
          .concat(postLoginRelativeRedirectUrl);
        // this is mostly a defensive check to replace any multiple question marks with a single one
        postLoginAbsoluteRedirectUrl = postLoginAbsoluteRedirectUrl.replace('?+', '?');

        const encodedPostLoginRedirectUrl = encodeURIComponent(postLoginAbsoluteRedirectUrl);
        redirectUrl = `${CONFIG.reactBaseUrl}login?next=${encodedPostLoginRedirectUrl}`;
      } else {
        redirectUrl = `${CONFIG.reactBaseUrl}login`;
      }

      // redirects to dynamic branch build feron apps post login
      if (/[A-Z]{2,}-\d+/.test(CONFIG.reactBaseUrl)) {
        const encodedDynamicFeronBaseUrl = encodeURIComponent(CONFIG.reactBaseUrl);
        redirectUrl = `${CONFIG.reactBaseUrl}login?next=${encodedDynamicFeronBaseUrl}`;
      }

      return <ExternalRedirect externalUrl={redirectUrl} />;
    }

    return null;
  };

  return <Route exact={exact} path={path} render={renderChildren} />;
};

export default AuthenticatedRoute as ComponentType<RouteProps>;
